<template>
  <div id="controls">
    <span v-show="votingStore.voteHistory.length && session.sessionId" class="nomlog-summary"
      :title="`${votingStore.voteHistory.length} recent ${votingStore.voteHistory.length === 1 ? 'nomination' : 'nominations'}`"
      @click="grimoire.toggleModal('voteHistory')">
      <font-awesome-icon icon="book-dead" class="fa fa-book-dead" />
      {{ votingStore.voteHistory.length }}
    </span>
    <span v-if="session.sessionId" class="session"
      :title="`${session.playerCount} other players in this session${session.ping ? ' (' + session.ping + 'ms latency)' : ''}`"
      :class="{ spectator: session.isPlayerOrSpectator, reconnecting: session.isReconnecting }"
      @click="session.leaveSession">
      <font-awesome-icon :icon="['fas', 'tower-broadcast']" class="fa fa-tower-broadcast" />
      {{ session.playerCount }}
    </span>
    <div class="menu" :class="{ open: userPreferences.isMenuOpen }">
      <font-awesome-icon icon="cog" class="fa fa-cog"
        @click="userPreferences.isMenuOpen = !userPreferences.isMenuOpen" />
      <ul>
        <li class="tabs" :class="tab">
          <font-awesome-icon :icon="['fas', 'tower-broadcast']" :title="sessionTabTitle" class="fa fa-tower-broadcast"
            :class="{ active: tab === 'session' }" @click="tab = 'session'" />
          <font-awesome-icon icon="bars-progress" :title="t('menu.tabs.options')" class="fa fa-bars-progress" :class="{ active: tab === 'options' }"
            @click="tab = 'options'" />
          <font-awesome-icon v-if="!session.isPlayerOrSpectator" icon="users" :title="t('menu.tabs.setup')" class="fa fa-users"
            :class="{ active: tab === 'setup' }" @click="tab = 'setup'" />
          <font-awesome-icon v-if="!session.isPlayerOrSpectator" icon="book-open" :title="t('menu.tabs.storytelling')" class="fa fa-book-open"
            :class="{ active: tab === 'storytelling' }" @click="tab = 'storytelling'" />
          <font-awesome-icon v-if="!session.isPlayerOrSpectator" icon="chair" :title="t('menu.tabs.playersMenu')" class="fa fa-chair"
            :class="{ active: tab === 'playersMenu' }" @click="tab = 'playersMenu'" />
          <font-awesome-icon v-if="!session.isPlayerOrSpectator" icon="music" :title="t('menu.tabs.soundboard')" class="fa fa-music"
            :class="{ active: tab === 'soundboard' }" @click="tab = 'soundboard'" />
          <font-awesome-icon icon="theater-masks" :title="t('menu.tabs.gameplay')" class="fa fa-theater-masks" :class="{ active: tab === 'gameplay' }"
            @click="tab = 'gameplay'" />
          <font-awesome-icon icon="question" :title="t('menu.tabs.about')" class="fa fa-question" :class="{ active: tab === 'about' }"
            @click="tab = 'about'" />
        </li>

        <MenuSession v-if="tab === 'session'" />
        <MenuOptions v-if="tab === 'options'" />
        <MenuSetup v-if="tab === 'setup' && !session.isPlayerOrSpectator" />
        <MenuStorytelling v-if="tab === 'storytelling' && !session.isPlayerOrSpectator" />
        <MenuPlayersMenu v-if="tab === 'playersMenu' && !session.isPlayerOrSpectator" />
        <MenuSoundboard v-if="tab === 'soundboard' && !session.isPlayerOrSpectator" />
        <MenuGameplay v-if="tab === 'gameplay'" />
        <MenuAbout v-if="tab === 'about'" />
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import {
  useGrimoireStore,
  useLocaleStore,
  useSessionStore,
  useUserPreferencesStore,
  useVotingStore,
} from "@/stores";
import MenuOptions from "./MenuOptions.vue";
import MenuPlayersMenu from "./MenuPlayersMenu.vue";
import MenuSession from "./MenuSession.vue";
import MenuSetup from "./MenuSetup.vue";
import MenuStorytelling from "./MenuStorytelling.vue";
import MenuSoundboard from "./MenuSoundboard.vue";
import MenuGameplay from "./MenuGameplay.vue";
import MenuAbout from './MenuAbout.vue';

const grimoire = useGrimoireStore();
const locale = useLocaleStore();
const session = useSessionStore();
const userPreferences = useUserPreferencesStore();
const votingStore = useVotingStore();
const t = locale.t;

type Tab = 'session' | 'options' | 'setup' | 'storytelling' | 'playersMenu' | 'soundboard' | 'gameplay' | 'about';
const tab = ref<Tab>('session');

// menu.tabs.session is a namespace ({ player, host, create }); pick the
// contextual label so the tooltip is a string (mirrors MenuSession.vue).
const sessionTabTitle = computed(() =>
  !session.sessionId
    ? t('menu.tabs.session.create')
    : session.isPlayerOrSpectator
      ? t('menu.tabs.session.player')
      : t('menu.tabs.session.host'),
);

</script>

<style lang="scss" src="./Menu.scss" scoped></style>
